/**
 * Company Profile Page
 *
 * Customer-facing page to view and edit organization profile.
 * Loads data from TOrganisation + TTicketOrgDetails via API.
 * Compliance status computed from real org documents.
 * Tax profile kept as display-only (dummy edit) for now.
 */

import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Grid,
  Divider,
  Icon,
  Chip,
  LinearProgress,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Button,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import WDBox from "../../../MaterialComponents/WDBox";
import WDTypography from "../../../MaterialComponents/WDTypography";
import WDButton from "../../../MaterialComponents/WDButton";
import {
  updateScreenTitle,
  showSuccessMessage,
  showErroMessage,
  showLoader,
  dismissLoader,
} from "../../../LayoutContainers/AppLayout/redux/AppSlice";
import axios from "axios";
import { API_ENDPOINTS } from "../../../Api/endpoints";
import { addAuthHeader } from "../../../utils/authUtils";

const getOrgId = () => {
  try {
    const raw = localStorage.getItem("authInfo");
    if (raw) {
      const info = JSON.parse(raw);
      return info?.principalInfo?.organisationId || info?.user?.organisation?.id;
    }
    return localStorage.getItem("organizationId");
  } catch {
    return null;
  }
};

// Helper: format date for display
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
};

// Helper: format date for input[type=date]
const toInputDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    return dt.toISOString().split("T")[0];
  } catch { return ""; }
};

// Field display row
const FieldRow = ({ label, value, xs = 6 }) => (
  <Grid item xs={xs}>
    <WDTypography variant="caption" color="text.secondary">{label}</WDTypography>
    <WDTypography variant="body2">{value || "—"}</WDTypography>
  </Grid>
);

// Section card with edit button
const SectionCard = ({ title, onEdit, children, action }) => (
  <Card sx={{ p: 3, height: "100%" }}>
    <WDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <WDTypography variant="h6" fontWeight="medium">{title}</WDTypography>
      {onEdit && (
        <IconButton size="small" onClick={onEdit}><Icon>edit</Icon></IconButton>
      )}
      {action}
    </WDBox>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Card>
);

const CompanyProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orgId = useMemo(getOrgId, []);

  // Data
  const [orgData, setOrgData] = useState(null); // from TOrganisation API (GET /v1/organisation)
  const [orgDetails, setOrgDetails] = useState(null); // from TTicketOrgDetails (GET /v1/organization-details/{orgId})
  const [compliance, setCompliance] = useState(null); // computed from org documents
  const [loading, setLoading] = useState(true);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editSection, setEditSection] = useState("company");
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [detailsRes, docsRes, requiredRes] = await Promise.allSettled([
        axios.get(API_ENDPOINTS.ORG_DETAILS.BY_ORG(orgId), { headers: addAuthHeader() }),
        axios.get(API_ENDPOINTS.ORGANIZATION_DOCUMENTS.GROUPED(orgId), { headers: addAuthHeader() }),
        axios.get(API_ENDPOINTS.BUSINESS_CATEGORIES.MY_REQUIRED_DOCUMENTS, { headers: addAuthHeader() }),
      ]);

      // Org details (combined TOrganisation + TTicketOrgDetails from backend toResponse)
      if (detailsRes.status === "fulfilled" && detailsRes.value?.data) {
        const d = detailsRes.value.data;
        setOrgDetails(d);
        // Also use some fields as org-level display
        setOrgData({
          name: d.companyNameEn,
          email: d.email,
          contactNumber: d.mobileNumber,
          address: d.address,
          tradeLicenseNumber: d.licenseNumber,
        });
      }

      // Compliance: match required document type IDs against uploaded document type IDs
      // Required docs response: array of { documentType: { id, code, name }, isMandatory, ... }
      // Grouped docs response: array of { common: { documentTypeId, ... }, documents: [...] }
      const requiredTypeIds = new Set();
      if (requiredRes.status === "fulfilled" && requiredRes.value?.data) {
        const required = Array.isArray(requiredRes.value.data) ? requiredRes.value.data : [];
        required.forEach((r) => {
          const typeId = r.documentType?.id;
          if (typeId) requiredTypeIds.add(typeId);
        });
      }

      const uploadedTypeIds = new Set();
      let expiringSoon = 0;
      let expired = 0;
      if (docsRes.status === "fulfilled" && docsRes.value?.data) {
        const raw = docsRes.value.data;
        const groups = Array.isArray(raw) ? raw : (Array.isArray(raw.groups) ? raw.groups : []);
        // Bucket by documentTypeId, pick current group per type (same as OrganizationDocumentsPage)
        const byType = {};
        groups.forEach((g) => {
          const typeId = g.common?.documentTypeId;
          if (!typeId) return;
          if (!byType[typeId]) byType[typeId] = [];
          byType[typeId].push(g);
        });

        Object.entries(byType).forEach(([typeId, typeGroups]) => {
          // Find current group
          let current = typeGroups.find((g) => g.common?.isCurrent === true);
          if (!current) {
            current = typeGroups.reduce((best, g) => {
              const bv = best.documents?.[0]?.version ?? 0;
              const gv = g.documents?.[0]?.version ?? 0;
              return gv > bv ? g : best;
            }, typeGroups[0]);
          }
          if (!current?.documents?.length) return;

          uploadedTypeIds.add(typeId);

          // Check expiry from current group's common metadata
          const expDate = current.common?.expiryDate;
          if (expDate) {
            const diffDays = Math.ceil((new Date(expDate) - new Date()) / (1000 * 60 * 60 * 24));
            if (diffDays < 0) expired++;
            else if (diffDays <= 30) expiringSoon++;
          }
        });
      }

      const totalRequired = requiredTypeIds.size;
      // Uploaded = how many required types have been uploaded
      let uploaded = 0;
      requiredTypeIds.forEach((id) => { if (uploadedTypeIds.has(id)) uploaded++; });
      const missing = totalRequired - uploaded;
      const pct = totalRequired > 0 ? Math.round((uploaded / totalRequired) * 100) : (uploadedTypeIds.size > 0 ? 100 : 0);
      setCompliance({ totalRequired, uploaded, missing, expiringSoon, expired, percentage: Math.min(pct, 100) });
    } catch (err) {
      console.error("Failed to load profile:", err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    dispatch(updateScreenTitle("Company Profile"));
    loadProfile();
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch, loadProfile]);

  // Edit handlers
  const openEdit = (section) => {
    setEditSection(section);
    setEditForm({ ...orgDetails });
    setEditOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(API_ENDPOINTS.ORG_DETAILS.BY_ORG(orgId), editForm, { headers: addAuthHeader() });
      dispatch(showSuccessMessage("Profile updated successfully"));
      setEditOpen(false);
      loadProfile();
    } catch (err) {
      console.error("Save failed:", err);
      dispatch(showErroMessage(err?.response?.data?.message || "Failed to save profile"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <WDBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </WDBox>
    );
  }

  if (!orgDetails) {
    return (
      <WDBox p={3}>
        <Alert severity="error">Failed to load organization profile. Please try again.</Alert>
      </WDBox>
    );
  }

  const d = orgDetails; // shorthand

  return (
    <WDBox>
      {/* Header Card */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Box sx={{ width: 80, height: 80, borderRadius: 2, backgroundColor: "grey.200",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon sx={{ fontSize: 40, color: "grey.500" }}>business</Icon>
            </Box>
          </Grid>
          <Grid item xs>
            <WDTypography variant="h5" fontWeight="medium">
              {d.companyNameEn || "Organization"}
            </WDTypography>
            {d.companyNameAr && (
              <WDTypography variant="body2" color="text.secondary">{d.companyNameAr}</WDTypography>
            )}
            <WDBox display="flex" gap={1} mt={1}>
              {d.legalForm && <Chip label={d.legalForm} size="small" variant="outlined" />}
              <Chip label="Active" color="success" size="small" />
            </WDBox>
          </Grid>
          <Grid item>
            <WDButton variant="outlined" color="info" startIcon={<Icon>edit</Icon>} onClick={() => openEdit("company")}>
              Edit Profile
            </WDButton>
          </Grid>
        </Grid>
      </Card>

      <Grid container spacing={3}>
        {/* Company Details */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Company Details" onEdit={() => openEdit("company")}>
            <Grid container spacing={2}>
              <FieldRow label="Company Name (English)" value={d.companyNameEn} />
              <FieldRow label="Company Name (Arabic)" value={d.companyNameAr} />
              <FieldRow label="Legal Form" value={d.legalForm} />
              <FieldRow label="Establishment Date" value={fmtDate(d.establishmentDate)} />
              <FieldRow label="Trade License No." value={d.licenseNumber} />
              <FieldRow label="License Authority" value={d.licenseAuthority} />
              <FieldRow label="License Issue Date" value={fmtDate(d.licenseIssueDate)} />
              <FieldRow label="License Expiry Date" value={fmtDate(d.licenseExpiryDate)} />
              <FieldRow label="Commercial Registration" value={d.commercialRegistration} />
              <FieldRow label="Address" value={d.address} xs={12} />
              <FieldRow label="Emirate" value={d.emirate} />
              <FieldRow label="Area" value={d.area} />
              <FieldRow label="PO Box" value={d.poBox} />
            </Grid>
          </SectionCard>
        </Grid>

        {/* Government Cards */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Government Cards" onEdit={() => openEdit("govCards")}>
            <Grid container spacing={2}>
              <FieldRow label="Establishment Card No." value={d.establishmentCardNumber} />
              <FieldRow label="Establishment Card Expiry" value={fmtDate(d.establishmentCardExpiry)} />
              <FieldRow label="Immigration Card No." value={d.immigrationCardNumber} />
              <FieldRow label="Immigration Card Expiry" value={fmtDate(d.immigrationCardExpiry)} />
              <FieldRow label="MOHRE License No." value={d.mohreLicenseNumber} />
              <FieldRow label="Chamber Membership No." value={d.chamberMembershipNumber} />
            </Grid>
          </SectionCard>
        </Grid>

        {/* Contact & Signatory */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Contact & Signatory" onEdit={() => openEdit("contact")}>
            <Grid container spacing={2}>
              <FieldRow label="Email" value={d.email} />
              <FieldRow label="Mobile Number" value={d.mobileNumber} />
              <FieldRow label="Land Number" value={d.landNumber} />
              <FieldRow label="Fax Number" value={d.faxNumber} />
            </Grid>
            <Divider sx={{ my: 2 }} />
            <WDTypography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Authorized Signatory</WDTypography>
            <Grid container spacing={2}>
              <FieldRow label="Name" value={d.authSignatoryName} />
              <FieldRow label="Designation" value={d.authSignatoryDesignation} />
              <FieldRow label="Emirates ID" value={d.authSignatoryEmiratesId} />
              <FieldRow label="Mobile" value={d.authSignatoryMobile} />
              <FieldRow label="Email" value={d.authSignatoryEmail} />
            </Grid>
          </SectionCard>
        </Grid>

        {/* Owner & Financial */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Owner & Financial" onEdit={() => openEdit("ownerFinancial")}>
            <WDTypography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Owner Information</WDTypography>
            <Grid container spacing={2}>
              <FieldRow label="Owner Name" value={d.ownerName} />
              <FieldRow label="Nationality" value={d.ownerNationality} />
              <FieldRow label="Passport Number" value={d.ownerPassportNumber} />
              <FieldRow label="Emirates ID" value={d.ownerEmiratesId} />
              <FieldRow label="Share %" value={d.ownerSharePercentage != null ? `${d.ownerSharePercentage}%` : null} />
            </Grid>
            <Divider sx={{ my: 2 }} />
            <WDTypography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Bank Details</WDTypography>
            <Grid container spacing={2}>
              <FieldRow label="Bank Name" value={d.bankName} />
              <FieldRow label="Account Number" value={d.accountNumber} />
              <FieldRow label="IBAN" value={d.iban} />
              <FieldRow label="SWIFT Code" value={d.swiftCode} />
            </Grid>
          </SectionCard>
        </Grid>

        {/* Compliance Status */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Compliance Status" action={
            <WDButton variant="text" size="small" onClick={() => navigate("/CST-OrgDocuments/list")}>
              View Documents
            </WDButton>
          }>
            {compliance ? (
              <>
                <WDBox mb={2}>
                  <WDBox display="flex" justifyContent="space-between" mb={1}>
                    <WDTypography variant="body2">Documents Compliance</WDTypography>
                    <WDTypography variant="body2" fontWeight="medium">{compliance.percentage}%</WDTypography>
                  </WDBox>
                  <LinearProgress variant="determinate" value={compliance.percentage}
                    sx={{ height: 10, borderRadius: 5, backgroundColor: "grey.200",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: compliance.percentage >= 80 ? "success.main"
                          : compliance.percentage >= 50 ? "warning.main" : "error.main",
                      },
                    }}
                  />
                </WDBox>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <WDTypography variant="caption" color="text.secondary">Required</WDTypography>
                    <WDTypography variant="h6">{compliance.totalRequired}</WDTypography>
                  </Grid>
                  <Grid item xs={3}>
                    <WDTypography variant="caption" color="text.secondary">Uploaded</WDTypography>
                    <WDTypography variant="h6" color="success.main">{compliance.uploaded}</WDTypography>
                  </Grid>
                  <Grid item xs={3}>
                    <WDTypography variant="caption" color="text.secondary">Missing</WDTypography>
                    <WDTypography variant="h6" color="error.main">{compliance.missing}</WDTypography>
                  </Grid>
                  <Grid item xs={3}>
                    <WDTypography variant="caption" color="text.secondary">Expired</WDTypography>
                    <WDTypography variant="h6" color="error.main">{compliance.expired}</WDTypography>
                  </Grid>
                </Grid>
                {compliance.missing > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {compliance.missing} required document(s) are missing.
                    <Button size="small" onClick={() => navigate("/CST-OrgDocuments/required")} sx={{ ml: 1 }}>
                      View Required
                    </Button>
                  </Alert>
                )}
              </>
            ) : (
              <WDTypography variant="body2" color="text.secondary">Unable to load compliance data</WDTypography>
            )}
          </SectionCard>
        </Grid>

        {/* Tax Profile (Dummy) */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Tax Profile">
            <Grid container spacing={2}>
              <FieldRow label="TRN Number" value={d.trnNumber} />
              <FieldRow label="VAT Registration No." value={d.vatRegistrationNumber} />
              <FieldRow label="VAT Registration Date" value={fmtDate(d.vatRegistrationDate)} />
              <FieldRow label="Corporate Tax Registration" value={d.corporateTaxRegistration} />
              <FieldRow label="Tax Year" value={d.taxYear} />
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit {editSection === "company" ? "Company Details" : editSection === "govCards" ? "Government Cards"
            : editSection === "contact" ? "Contact & Signatory" : "Owner & Financial"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {editSection === "company" && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Company Name (Arabic)" size="small"
                    value={editForm.companyNameAr || ""} onChange={(e) => handleFieldChange("companyNameAr", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Legal Form" size="small"
                    value={editForm.legalForm || ""} onChange={(e) => handleFieldChange("legalForm", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Establishment Date" type="date" size="small" InputLabelProps={{ shrink: true }}
                    value={toInputDate(editForm.establishmentDate)} onChange={(e) => handleFieldChange("establishmentDate", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="License Authority" size="small"
                    value={editForm.licenseAuthority || ""} onChange={(e) => handleFieldChange("licenseAuthority", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="License Issue Date" type="date" size="small" InputLabelProps={{ shrink: true }}
                    value={toInputDate(editForm.licenseIssueDate)} onChange={(e) => handleFieldChange("licenseIssueDate", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="License Expiry Date" type="date" size="small" InputLabelProps={{ shrink: true }}
                    value={toInputDate(editForm.licenseExpiryDate)} onChange={(e) => handleFieldChange("licenseExpiryDate", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Commercial Registration" size="small"
                    value={editForm.commercialRegistration || ""} onChange={(e) => handleFieldChange("commercialRegistration", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Emirate" size="small"
                    value={editForm.emirate || ""} onChange={(e) => handleFieldChange("emirate", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Area" size="small"
                    value={editForm.area || ""} onChange={(e) => handleFieldChange("area", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="PO Box" size="small"
                    value={editForm.poBox || ""} onChange={(e) => handleFieldChange("poBox", e.target.value)} />
                </Grid>
              </Grid>
            )}

            {editSection === "govCards" && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Establishment Card No." size="small"
                    value={editForm.establishmentCardNumber || ""} onChange={(e) => handleFieldChange("establishmentCardNumber", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Establishment Card Expiry" type="date" size="small" InputLabelProps={{ shrink: true }}
                    value={toInputDate(editForm.establishmentCardExpiry)} onChange={(e) => handleFieldChange("establishmentCardExpiry", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Immigration Card No." size="small"
                    value={editForm.immigrationCardNumber || ""} onChange={(e) => handleFieldChange("immigrationCardNumber", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Immigration Card Expiry" type="date" size="small" InputLabelProps={{ shrink: true }}
                    value={toInputDate(editForm.immigrationCardExpiry)} onChange={(e) => handleFieldChange("immigrationCardExpiry", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="MOHRE License No." size="small"
                    value={editForm.mohreLicenseNumber || ""} onChange={(e) => handleFieldChange("mohreLicenseNumber", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Chamber Membership No." size="small"
                    value={editForm.chamberMembershipNumber || ""} onChange={(e) => handleFieldChange("chamberMembershipNumber", e.target.value)} />
                </Grid>
              </Grid>
            )}

            {editSection === "contact" && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Land Number" size="small"
                    value={editForm.landNumber || ""} onChange={(e) => handleFieldChange("landNumber", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Fax Number" size="small"
                    value={editForm.faxNumber || ""} onChange={(e) => handleFieldChange("faxNumber", e.target.value)} />
                </Grid>
                <Grid item xs={12}><Divider sx={{ my: 1 }} /><WDTypography variant="subtitle2">Authorized Signatory</WDTypography></Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Signatory Name" size="small"
                    value={editForm.authSignatoryName || ""} onChange={(e) => handleFieldChange("authSignatoryName", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Designation" size="small"
                    value={editForm.authSignatoryDesignation || ""} onChange={(e) => handleFieldChange("authSignatoryDesignation", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Emirates ID" size="small"
                    value={editForm.authSignatoryEmiratesId || ""} onChange={(e) => handleFieldChange("authSignatoryEmiratesId", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Mobile" size="small"
                    value={editForm.authSignatoryMobile || ""} onChange={(e) => handleFieldChange("authSignatoryMobile", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Email" size="small"
                    value={editForm.authSignatoryEmail || ""} onChange={(e) => handleFieldChange("authSignatoryEmail", e.target.value)} />
                </Grid>
              </Grid>
            )}

            {editSection === "ownerFinancial" && (
              <Grid container spacing={2}>
                <Grid item xs={12}><WDTypography variant="subtitle2">Owner Information</WDTypography></Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Owner Name" size="small"
                    value={editForm.ownerName || ""} onChange={(e) => handleFieldChange("ownerName", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Nationality" size="small"
                    value={editForm.ownerNationality || ""} onChange={(e) => handleFieldChange("ownerNationality", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Passport Number" size="small"
                    value={editForm.ownerPassportNumber || ""} onChange={(e) => handleFieldChange("ownerPassportNumber", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Emirates ID" size="small"
                    value={editForm.ownerEmiratesId || ""} onChange={(e) => handleFieldChange("ownerEmiratesId", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Share %" type="number" size="small"
                    value={editForm.ownerSharePercentage ?? ""} onChange={(e) => handleFieldChange("ownerSharePercentage", e.target.value ? parseInt(e.target.value) : null)} />
                </Grid>
                <Grid item xs={12}><Divider sx={{ my: 1 }} /><WDTypography variant="subtitle2">Bank Details</WDTypography></Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Bank Name" size="small"
                    value={editForm.bankName || ""} onChange={(e) => handleFieldChange("bankName", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Account Number" size="small"
                    value={editForm.accountNumber || ""} onChange={(e) => handleFieldChange("accountNumber", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="IBAN" size="small"
                    value={editForm.iban || ""} onChange={(e) => handleFieldChange("iban", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="SWIFT Code" size="small"
                    value={editForm.swiftCode || ""} onChange={(e) => handleFieldChange("swiftCode", e.target.value)} />
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <WDButton variant="gradient" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Save Changes"}
          </WDButton>
        </DialogActions>
      </Dialog>
    </WDBox>
  );
};

export default CompanyProfilePage;
